import React, { useEffect, useState } from 'react';
import useStyles from 'isomorphic-style-loader/useStyles';

import { useContainerData } from '../../../../shared/public/SlHooks/useContainerData';
import { TranslationIcon } from './TranslationIcon';
import { CourseTranslationModal } from './CourseTranslationModal';
import { SlDataService } from '../../services/sl-data.service';
import { SlActions } from '../../services/ActionsService/sl-actions';
import { ActionType, RequestType } from '../../global-interface';
import { SlLEContext } from '../../global-constants';

import s from './CourseTranslation.scss';
import { Container, i18n } from '../../../../symphony';

export function CourseTranslation(): JSX.Element {
	useStyles(s);
	const lastTranslatedLanguageLocalStorageKey = 'lastTranslatedLanguage';
	const { actions$ } = Container.take(SlLEContext, SlActions);
	const {
		course$: course,
		isTranslationModalOpened$: isTranslationModalOpened,
	} = useContainerData(SlLEContext, SlDataService, [
		'course$',
		'isTranslationModalOpened$',
	]);

	const getLastTranslatedLanguageLocalStorageData = () => {
		try {
			const data = JSON.parse(
				localStorage.getItem(lastTranslatedLanguageLocalStorageKey),
			);

			return data || {};
		} catch (error) {
			return {};
		}
	};

	const [lastTranslatedLanguage, setLastTranslatedLanguage] = useState(
		getLastTranslatedLanguageLocalStorageData()?.[
			course?.materialInfo?.header?.id
		],
	);

	const defaultLanguage = course?.materialInfo?.defaultLanguage;

	const courseLanguage = course?.supportedLocales?.find(
		(supportedLocale) => supportedLocale.isSelected,
	)?.locale || defaultLanguage;
	const [selectedLocal, setSelectedLocal] = useState(courseLanguage);

	const updateLastTranslatedLanguage = (language: string) => {
		setLastTranslatedLanguage(language);
		const lastTranslatedLanguageLS = getLastTranslatedLanguageLocalStorageData();
		lastTranslatedLanguageLS[course?.materialInfo?.header?.id] = language;
		localStorage.setItem(
			lastTranslatedLanguageLocalStorageKey,
			JSON.stringify(lastTranslatedLanguageLS),
		);
	};

	const openTranslationModalClickHandler = () => {
		actions$.next({
			type: ActionType.OnOpenTranslationModalClick,
			requestType: RequestType.Parallel,
		});
	};

	const seeOriginalLanguageButtonClickHandler = () => {
		actions$.next({
			type: ActionType.OnSeeOriginalLanguageButtonClickHandler,
			requestType: RequestType.Parallel,
		});
	};

	const seeTranslatedLanguageButtonClickHandler = () => {
		actions$.next({
			type: ActionType.OnSeeTranslatedLanguageButtonClickHandler,
			requestType: RequestType.Parallel,
			data: {
				locale: lastTranslatedLanguage,
			},
		});
	};

	useEffect(() => {
		if (isTranslationModalOpened) {
			setSelectedLocal(courseLanguage);
		}
	}, [isTranslationModalOpened]);

	useEffect(() => {
		if (
			courseLanguage !== defaultLanguage
			&& courseLanguage !== lastTranslatedLanguage
		) {
			updateLastTranslatedLanguage(courseLanguage);
		}
	}, [courseLanguage]);

	return (
		<>
			<div className="le-course-translation">
				<div className="le-course-translation__inner">
					<div
						className="le-course-translation__icon"
						onClick={openTranslationModalClickHandler}
					>
						<TranslationIcon />
					</div>
					{courseLanguage !== defaultLanguage && (
						<div
							className="le-course-translation__translate-button"
							onClick={seeOriginalLanguageButtonClickHandler}
						>
							{i18n.t('web-le.translate-course-see-original')}
						</div>
					)}
					{lastTranslatedLanguage
						&& courseLanguage === defaultLanguage && (
						<div
							className="le-course-translation__translate-button"
							onClick={
								seeTranslatedLanguageButtonClickHandler
							}
						>
							{i18n.t(
								'web-le.translate-course-see-translation',
							)}
						</div>
					)}
					{(lastTranslatedLanguage
						|| courseLanguage !== defaultLanguage) && (
						<div className="le-course-translation__separator">
							.
						</div>
					)}
					<div
						className="le-course-translation__translate-button"
						onClick={openTranslationModalClickHandler}
					>
						{lastTranslatedLanguage
							|| courseLanguage !== defaultLanguage
							? i18n.t(
								'web-le.translate-course-language-settings',
							)
							: i18n.t('web-le.translate-course-button')}
					</div>
				</div>
			</div>
			{isTranslationModalOpened && (
				<CourseTranslationModal
					selectedLocal={selectedLocal}
					setSelectedLocal={setSelectedLocal}
					updateLastTranslatedLanguage={updateLastTranslatedLanguage}
					defaultLanguage={defaultLanguage}
					courseLanguage={courseLanguage}
				/>
			)}
		</>
	);
}
